import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  Wallet,
  Search,
  Plus,
  FileText,
  Trash2,
  CheckCircle,
  XCircle,
  Clock,
  FileSpreadsheet,
  RefreshCcw,
  LayoutGrid,
  Shield,
  Layers,
  DollarSign,
  Landmark,
  CreditCard,
  TrendingUp,
  Banknote
} from 'lucide-react';
import QawaemPageTitleWithFilters from '../../products/saudi/components/QawaemPageTitleWithFilters';
import { generateReportPDF } from '../../shared/utils/reportPdfGenerator';
import { generateReportExcel } from '../../shared/utils/reportExcelGeneratorAdvanced';
import { formatBrandMoney, getBrandDefaultCurrency, getBrandCurrencySuffix } from '../../shared/utils/brandCurrency';
import featureService from '../../core/services/featureService';
import { shouldShowCardsToolbarSearch } from '../../shared/utils/listPageSearchVisibility';
import useModuleConfigTheme from '../../shared/hooks/useModuleConfigTheme';
import QawaemErpCrudModal, { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import { QawaemErpModalFormShell } from '../../products/saudi/components/QawaemErpModalFormTabs';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import {
  QawaemTableSelectAllHeader,
  QawaemTableSelectRowCell,
  useQawaemFilteredRowSelection,
} from '../../products/saudi/components/QawaemTableSelection';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import useColumnHeaderFilters from '../../shared/hooks/useColumnHeaderFilters';
import applyColumnFilters, { textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import { buildCaisseColumnDefs, useCaisseColumnVisibility, caisseTableColSpan, buildCaisseModalFormTabs, sliceCaisseListItems, dispatchCaisseAccountsRefresh, CAISSE_FORM_GRID } from './caisseListPageUtils';
import { useTreasuryConfig } from './useTreasuryConfig';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import { QawaemTableStickyActionsTd } from '../../products/saudi/components/QawaemTableStickyActions';
import localApiAdapter from '../../core/services/localApiAdapter';
import {
  BANQUE_ACCOUNTS_TREASURY_SYNC_EVENT,
  mapBankAccountForTreasury,
  syncBanqueAccountsForTreasury,
} from '../banque/bankingTreasurySync';

const TreasuryPage = () => {
  const { t } = useTranslation();
  const treasuryConfig = useTreasuryConfig();
  const p = (key, opts) => t(`modules.caisse.pages.treasuryPage.${key}`, opts);
  const cfgTheme = useModuleConfigTheme();
  const isLight = cfgTheme.light;
  const hasCaisseModule = featureService.isFeatureEnabled('/caisse');
  const hasBanqueModule = featureService.isFeatureEnabled('/banque');
  const isAutoMode = hasCaisseModule && hasBanqueModule;
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const entityLabel = p('entity_label', { defaultValue: 'الحساب' });
  
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');

  const { filters, setFilter, editingKey, setEditingKey } = useColumnHeaderFilters();

  const columnFilterRules = useMemo(() => [
    textColumnRule('type', (r) => r.type || ''),
    textColumnRule('name', (r) => r.name || ''),
    textColumnRule('bank', (r) => r.bank || ''),
    textColumnRule('accountNumber', (r) => r.accountNumber || ''),
    textColumnRule('balance', (r) => String(r.balance ?? '')),
    exactColumnRule('status', (r) => r.status || ''),
  ], []);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [viewingAccount, setViewingAccount] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [mainViewMode, setMainViewMode] = useState('all_accounts');
  const [quickFilter, setQuickFilter] = useState('all');
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);
  const [caisseAmount, setCaisseAmount] = useState(0);
  
  const [accountFormTab, setAccountFormTab] = useState('account');

  const accountFormTabDefs = useMemo(() => ([
    { id: 'account', label: p('label_account_name').replace(' *', ''), icon: Wallet, iconClassName: 'text-blue-400' },
    { id: 'balance', label: p('label_initial_balance').replace(' *', ''), icon: DollarSign, iconClassName: 'text-green-500' },
  ]), [p]);
  const TREASURY_ACCOUNT_FIELD_COUNT = 6;
  const accountFormTabs = useMemo(
    () => buildCaisseModalFormTabs(accountFormTabDefs, TREASURY_ACCOUNT_FIELD_COUNT),
    [accountFormTabDefs],
  );

  const [formData, setFormData] = useState({
    type: 'banque',
    name: '',
    accountNumber: '',
    balance: 0,
    bank: '',
    notes: ''
  });

  useEffect(() => {
    loadTreasuryData();
  }, [refreshTrigger, isAutoMode]);

  useEffect(() => {
    if (!isAutoMode) return undefined;
    const onSync = () => loadTreasuryData();
    window.addEventListener(BANQUE_ACCOUNTS_TREASURY_SYNC_EVENT, onSync);
    return () => window.removeEventListener(BANQUE_ACCOUNTS_TREASURY_SYNC_EVENT, onSync);
  }, [isAutoMode, refreshTrigger]);

  useEffect(() => {
    if (!showModal && !showViewModal) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [showModal, showViewModal]);

  const loadTreasuryData = async () => {
    if (isAutoMode) {
      const caisseData = localStorage.getItem('caisseBalance');
      setCaisseAmount(caisseData ? parseFloat(caisseData) : 0);
      try {
        const result = await localApiAdapter.get('/banking/accounts');
        const raw = Array.isArray(result) ? result : result?.data || [];
        const mapped = raw.map(mapBankAccountForTreasury).filter(Boolean);
        syncBanqueAccountsForTreasury(raw);
        setAccounts(sortRecordsNewestFirst(mapped));
      } catch (error) {
        console.error('Treasury: failed to load bank accounts from API, using cache:', error);
        const banqueData = localStorage.getItem('banqueAccounts');
        setAccounts(sortRecordsNewestFirst(banqueData ? JSON.parse(banqueData) : []));
      }
      return;
    }
    const savedData = localStorage.getItem('manualTreasuryData');
    if (savedData) {
      const parsed = JSON.parse(savedData);
      setCaisseAmount(parsed.caisse || 0);
      setAccounts(sortRecordsNewestFirst(parsed.banques || []));
    }
  };

  const saveTreasuryData = (newAccounts, newCaisse = caisseAmount) => {
    const sortedAccounts = sortRecordsNewestFirst(newAccounts);
    if (!isAutoMode) {
      localStorage.setItem('manualTreasuryData', JSON.stringify({
        caisse: newCaisse,
        banques: sortedAccounts
      }));
    }
    setAccounts(sortedAccounts);
    setCaisseAmount(newCaisse);
  };

  const totalBanques = accounts.reduce((sum, acc) => sum + (parseFloat(acc.balance) || 0), 0);
  const totalTreasury = caisseAmount + totalBanques;

  const stats = {
    total: totalTreasury,
    caisse: caisseAmount,
    banques: totalBanques,
    accountsCount: accounts.length,
    avgBalance: accounts.length > 0 ? totalBanques / accounts.length : 0
  };

  const treasuryDistribution = {
    all_accounts: {
      titleKey: 'distribution_overview',
      data: [
        { nameKey: 'dist_caisse', count: 1, amount: caisseAmount, color: "bg-gradient-to-r from-green-500 to-green-600" },
        { nameKey: 'dist_bank_accounts', count: accounts.length, amount: totalBanques, color: "bg-gradient-to-r from-blue-500 to-blue-600" }
      ]
    },
    par_type: {
      titleKey: 'distribution_by_type',
      data: [
        { nameKey: 'dist_caisse', count: 1, amount: caisseAmount, color: "bg-gradient-to-r from-green-500 to-green-600" },
        { nameKey: 'dist_banks', count: accounts.length, amount: totalBanques, color: "bg-gradient-to-r from-blue-500 to-blue-600" }
      ]
    }
  };

  const distributionInfo = treasuryDistribution[mainViewMode] || treasuryDistribution.all_accounts;

  const mainTabs = [
    { id: 'all_accounts', nameKey: 'tab_all_accounts', icon: LayoutGrid },
    { id: 'par_type', nameKey: 'tab_by_type', icon: Shield }
  ];

  const quickFilterButtons = [
    { id: 'all', nameKey: 'quick_filter_all', icon: Wallet },
    { id: 'banque', nameKey: 'filter_banks', icon: Landmark },
    { id: 'caisse', nameKey: 'filter_caisse', icon: Banknote }
  ];

  const filteredAccounts = applyColumnFilters(
    accounts.filter((account) => {
      const matchesSearch = !searchTerm ||
        account.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        account.bank?.toLowerCase().includes(searchTerm.toLowerCase());

      if (!matchesSearch) return false;
      if (typeFilter !== 'all' && account.type !== typeFilter) return false;

      return true;
    }),
    filters,
    columnFilterRules,
  );
  const pagedAccounts = useMemo(
    () => sliceCaisseListItems(filteredAccounts, treasuryConfig.itemsPerPage),
    [filteredAccounts, treasuryConfig.itemsPerPage],
  );

  const {
    selectedRecords: selectedAccounts,
    allRowsSelected,
    someRowsSelected,
    handleSelectAll,
    handleSelectRecord: handleSelectAccount,
    clearSelection,
  } = useQawaemFilteredRowSelection(filteredAccounts);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name) {
      notifyError(showNotify, p('alert_name_required'));
      return;
    }

    if (editingAccount) {
      const updated = accounts.map(a => a.id === editingAccount.id ? { ...formData, id: a.id, updated_at: new Date().toISOString() } : a);
      saveTreasuryData(updated);
      notifyUpdated(showNotify, entityLabel);
    } else {
      saveTreasuryData([...accounts, { ...formData, id: Date.now(), dateAdded: new Date().toISOString(), created_at: new Date().toISOString() }]);
      notifyCreated(showNotify, entityLabel);
    }
    resetForm();
    triggerRefresh();
    dispatchCaisseAccountsRefresh();
  };

  const handleEdit = (account) => {
    setEditingAccount(account);
    setFormData(account);
    setAccountFormTab('account');
    setShowModal(true);
  };

  const handleDelete = (id) => {
    if (window.confirm(p('confirm_delete_account'))) {
      saveTreasuryData(accounts.filter(a => a.id !== id));
      notifyDeleted(showNotify, entityLabel);
      triggerRefresh();
      dispatchCaisseAccountsRefresh();
    }
  };

  const handleView = (account) => {
    setViewingAccount(account);
    setShowViewModal(true);
  };

  const clickableStatusOptions = useMemo(() => ([
    { value: 'active', label: t('common.active'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'inactive', label: t('common.inactive'), icon: XCircle, style: 'bg-gray-500/20 text-gray-400 border-gray-500/30' },
  ]), [t]);

  const treasuryColumnKeys = useMemo(
    () => ['type', 'name', 'bank', 'accountNumber', 'balance', 'status', 'actions'],
    [],
  );
  const { columnVisibility, handleToggleColumn, isColumnVisible } = useCaisseColumnVisibility(treasuryColumnKeys);
  const qawaemColumns = useMemo(
    () => buildCaisseColumnDefs([
      { key: 'type', labelKey: 'col_type' },
      { key: 'name', labelKey: 'col_name' },
      { key: 'bank', labelKey: 'col_bank' },
      { key: 'accountNumber', labelKey: 'col_account_number' },
      { key: 'balance', labelKey: 'col_balance' },
      { key: 'status', label: t('common.status') },
      { key: 'actions', label: t('common.actions'), required: true },
    ], p, columnVisibility),
    [p, t, columnVisibility],
  );
  const qawaemVisibleColumnCount = qawaemColumns.filter((c) => c.checked !== false).length;
  const tableColSpan = caisseTableColSpan(qawaemColumns);

  const normalizeAccountStatus = (account) => account?.status || 'active';

  const handleStatusChange = (recordId, newStatus) => {
    if (recordId === 'caisse-principale') return;
    const updated = accounts.map((a) => (a.id === recordId ? { ...a, status: newStatus } : a));
    saveTreasuryData(updated);
    triggerRefresh();
  };

  const resetForm = () => {
    setFormData({ type: 'banque', name: '', accountNumber: '', balance: 0, bank: '', notes: '' });
    setEditingAccount(null);
    setAccountFormTab('account');
    setShowModal(false);
  };

  const triggerRefresh = () => setRefreshTrigger(prev => prev + 1);

  const handleBulkDelete = () => {
    if (window.confirm(p('confirm_bulk_delete', { count: selectedAccounts.length }))) {
      saveTreasuryData(accounts.filter(a => !selectedAccounts.includes(a.id)));
      clearSelection();
      triggerRefresh();
    }
  };

  const handleExportPDF = async () => {
    try {
      setIsGeneratingPDF(true);
      const reportData = [
        { [p('export_col_type')]: p('type_caisse'), [p('export_col_name')]: p('main_caisse_name'), [p('export_col_balance')]: formatBrandMoney(caisseAmount) },
        ...filteredAccounts.map(a => ({
          [p('export_col_type')]: p('type_bank'),
          [p('export_col_name')]: a.name,
          [p('export_col_bank')]: a.bank,
          [p('export_col_account_number')]: a.accountNumber,
          [p('export_col_balance')]: formatBrandMoney(a.balance)
        }))
      ];
      await generateReportPDF(p('report_title'), reportData, { orientation: 'landscape' });
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      setIsGeneratingExcel(true);
      const reportData = [
        { [p('export_col_type')]: p('type_caisse'), [p('export_col_name')]: p('main_caisse_name'), [p('export_col_balance')]: caisseAmount },
        ...filteredAccounts.map(a => ({
          [p('export_col_type')]: p('type_bank'),
          [p('export_col_name')]: a.name,
          [p('export_col_bank')]: a.bank,
          [p('export_col_account_number')]: a.accountNumber,
          [p('export_col_balance')]: a.balance
        }))
      ];
      await generateReportExcel(p('report_title'), reportData);
    } finally {
      setIsGeneratingExcel(false);
    }
  };

  return (
    <div className={`space-y-6 employees-container`}>
      <div className="flex justify-between items-center">
        <div className="flex-1 min-w-0">
          <QawaemPageTitleWithFilters
            filters={selectedAccounts.length > 0 ? (
              <button
                type="button"
                onClick={handleBulkDelete}
                className="flex items-center gap-2 px-3 py-2 rounded-xl border border-red-500/35 bg-red-500/10 text-red-700 hover:bg-red-500/20 transition-colors text-sm font-medium shrink-0"
                title={p('delete_selection')}
              >
                <Trash2 size={16} />
                <span>{t('common.delete')} ({selectedAccounts.length})</span>
              </button>
            ) : null}
          >
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <div className="p-3 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700"><Wallet className="h-8 w-8" /></div>
            {p('title')}
            <button onClick={triggerRefresh} className="refresh-button"><RefreshCcw size={20} /></button>
          </h1>
          <p className="text-white/60 mt-2">
            {isAutoMode ? p('subtitle_auto') : p('subtitle_manual')}
          </p>
          </QawaemPageTitleWithFilters>
        </div>
        <button className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-blue-800 transition-all duration-300 flex items-center space-x-2" onClick={() => { setEditingAccount(null); setAccountFormTab('account'); setShowModal(true); }}>
          <Plus className="h-5 w-5" /><span>{p('new_account')}</span>
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { titleKey: 'stat_total_treasury', value: formatBrandMoney(stats.total), icon: Wallet, color: 'from-blue-500 to-blue-600' },
          { titleKey: 'stat_caisse', value: formatBrandMoney(stats.caisse), icon: Banknote, color: 'from-green-500 to-green-600' },
          { titleKey: 'stat_banks', value: formatBrandMoney(stats.banques), icon: Landmark, color: 'from-purple-500 to-purple-600' },
          { titleKey: 'stat_bank_accounts', value: stats.accountsCount, icon: CreditCard, color: 'from-orange-500 to-orange-600' },
          { titleKey: 'stat_avg_balance', value: formatBrandMoney(stats.avgBalance), icon: TrendingUp, color: 'from-indigo-500 to-indigo-600' }
        ].slice(0, 4).map((stat, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="relative group">
            <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`}></div>
            <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm font-medium">{p(stat.titleKey)}</p>
                  <p className="text-2xl font-bold text-white force-white mt-2">{stat.value}</p>
                </div>
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-lg`}></div>
                  <div className={`relative p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}><stat.icon className="h-6 w-6 text-white force-white" /></div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
        <div className="flex space-x-1 overflow-x-auto table-scroll">
          {mainTabs.map((tab) => (
            <button key={tab.id} onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); ; }}
              className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
              <tab.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{p(tab.nameKey)}</span>
            </button>
          ))}
        </div>
      </div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        <h3 className="text-xl font-semibold text-white mb-4">{p(distributionInfo.titleKey)}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {distributionInfo.data.map((item) => {
            const pct = totalTreasury > 0 ? (item.amount / totalTreasury * 100).toFixed(1) : 0;
            return (
            <div key={item.nameKey} className="bg-white/5 rounded-xl p-4 border border-white/10">
              <div className="flex justify-between items-center mb-2">
                <h4 className="font-medium text-white">{p(item.nameKey)}</h4>
                <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white`}>{formatBrandMoney(item.amount)}</div>
              </div>
              <div className="w-full bg-white/10 h-3 rounded-full">
                <div className={`h-3 rounded-full ${item.color}`} style={{ width: `${pct}%` }}></div>
              </div>
              <p className="mt-2 text-sm text-white/60">{p('percent_of_total', { percent: pct })}</p>
            </div>
          );})}
        </div>
      </motion.div>

      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          

          {shouldShowCardsToolbarSearch('table') ? (
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-3 h-5 w-5 text-white/50" />
              <input type="text" placeholder={p('search_account_placeholder')} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
          ) : null}

          <div className="flex items-center space-x-2">
            <button onClick={handleExportPDF} disabled={isGeneratingPDF || isGeneratingExcel}
              className={`p-2 rounded-lg transition-colors flex items-center gap-1 text-sm ${isGeneratingPDF ? 'bg-blue-600/50 text-white/50 cursor-not-allowed' : 'bg-gradient-to-r from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 text-white'}`}>
              <FileText size={16} /><span className="hidden md:inline">{isGeneratingPDF ? p('export_pdf_loading') : p('export_pdf')}</span>
            </button>
            <button onClick={handleExportExcel} disabled={isGeneratingPDF || isGeneratingExcel}
              className={`p-2 rounded-lg transition-colors flex items-center gap-1 text-sm ${isGeneratingExcel ? 'bg-green-600/50 text-white/50 cursor-not-allowed' : 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white'}`}>
              <FileSpreadsheet size={16} /><span className="hidden md:inline">{isGeneratingExcel ? p('export_excel_loading') : p('export_excel')}</span>
            </button>
          </div>
        </div>

        <div className="overflow-x-auto table-horizontal-scroll">
          <table className="w-full employees-table data-table">
            <thead className="glass-table-head">
              <tr>
                <QawaemTableSelectAllHeader
                  allRowsSelected={allRowsSelected}
                  someRowsSelected={someRowsSelected}
                  onChange={handleSelectAll}
                  selectAllTitle={t('common.select_all', { defaultValue: 'تحديد الكل' })}
                  deselectAllTitle={t('common.deselect_all', { defaultValue: 'إلغاء التحديد' })}
                />
                {isColumnVisible('type') ? (
                  <TableColumnHeaderFilter columnKey="type" label={p('col_type')} value={filters.type ?? ''} onChange={(v) => setFilter('type', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold" />
                ) : null}
                {isColumnVisible('name') ? (
                  <TableColumnHeaderFilter columnKey="name" label={p('col_name')} value={filters.name ?? ''} onChange={(v) => setFilter('name', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold" />
                ) : null}
                {isColumnVisible('bank') ? (
                  <TableColumnHeaderFilter columnKey="bank" label={p('col_bank')} value={filters.bank ?? ''} onChange={(v) => setFilter('bank', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold" />
                ) : null}
                {isColumnVisible('accountNumber') ? (
                  <TableColumnHeaderFilter columnKey="accountNumber" label={p('col_account_number')} value={filters.accountNumber ?? ''} onChange={(v) => setFilter('accountNumber', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold" />
                ) : null}
                {isColumnVisible('balance') ? (
                  <TableColumnHeaderFilter columnKey="balance" label={p('col_balance')} value={filters.balance ?? ''} onChange={(v) => setFilter('balance', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-white font-semibold" />
                ) : null}
                {isColumnVisible('status') ? (
                  <TableColumnHeaderFilter columnKey="status" label={t('common.status')} kind="select" options={[{ value: 'active', label: p('status_active') }, { value: 'inactive', label: p('status_inactive') }]} value={filters.status ?? ''} onChange={(v) => setFilter('status', v)} align="center" editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold" />
                ) : null}
                <QawaemTableHeaderActionsCell
                    thClassName="px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle"
                  label={t('common.actions')}
                  columns={qawaemColumns}
                  onToggle={handleToggleColumn}
                  visibleCount={qawaemVisibleColumnCount}
                  totalCount={qawaemColumns.length}
                />
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10 employees-table-body">
              <tr className="hover:bg-white/5 transition-colors">
                <td className="px-3 py-4 text-center align-middle"><span className="inline-block w-[18px]" aria-hidden /></td>
                {isColumnVisible('type') ? (
                  <td className="px-6 py-4"><span className="px-3 py-1.5 rounded-full text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/30 flex items-center gap-1.5 w-fit"><Banknote size={14} />{p('type_caisse')}</span></td>
                ) : null}
                {isColumnVisible('name') ? (
                  <td className="px-6 py-4 text-white font-semibold">{p('main_caisse_name')}</td>
                ) : null}
                {isColumnVisible('bank') ? (
                  <td className="px-6 py-4 text-white/80">-</td>
                ) : null}
                {isColumnVisible('accountNumber') ? (
                  <td className="px-6 py-4 text-white/80">-</td>
                ) : null}
                {isColumnVisible('balance') ? (
                  <td className="px-6 py-4 text-center text-white font-bold">{formatBrandMoney(caisseAmount)}</td>
                ) : null}
                {isColumnVisible('status') ? (
                  <td className="px-6 py-4 text-center" data-label={t('common.status')}>
                    <div className="flex justify-center">
                      <QawaemClickableStatusBadge
                        status="active"
                        recordId="caisse-principale"
                        options={clickableStatusOptions}
                        title={t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' })}
                      />
                    </div>
                  </td>
                ) : null}
                <QawaemTableStickyActionsTd>
                  <QawaemErpRowActions
                    onView={() => handleView({
                      id: 'caisse-principale',
                      type: 'caisse',
                      name: p('main_caisse_name'),
                      initialBalance: caisseAmount,
                      currentBalance: caisseAmount,
                      description: p('main_caisse_description'),
                      created_at: new Date().toISOString(),
                    })}
                    onEdit={() => handleEdit({
                      id: 'caisse-principale',
                      type: 'caisse',
                      name: p('main_caisse_name'),
                      balance: caisseAmount,
                    })}
                    onDelete={() => {
                      if (window.confirm(p('confirm_delete_main_caisse'))) {
                        localStorage.setItem('caisse', '0');
                        window.location.reload();
                      }
                    }}
                    viewTitle={p('view_details_title')}
                    editTitle={t('common.edit')}
                    deleteTitle={t('common.delete')}
                  />
                </QawaemTableStickyActionsTd>
              </tr>
              {filteredAccounts.length > 0 ? (
                pagedAccounts.map((account, index) => (
                  <tr key={account.id}
                    className={`hover:bg-white/5 transition-colors ${selectedAccounts.includes(account.id) ? 'bg-white/10' : ''}`}>
                    <QawaemTableSelectRowCell
                      checked={selectedAccounts.includes(account.id)}
                      onChange={() => handleSelectAccount(account.id)}
                    />
                    {isColumnVisible('type') ? (
                      <td className="px-6 py-4" data-label={p('col_type')}><span className="px-3 py-1.5 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400 border border-blue-500/30 flex items-center gap-1.5 w-fit"><Landmark size={14} />{p('type_bank')}</span></td>
                    ) : null}
                    {isColumnVisible('name') ? (
                      <td className="px-6 py-4 text-white font-semibold" data-label={p('col_name')}>{account.name}</td>
                    ) : null}
                    {isColumnVisible('bank') ? (
                      <td className="px-6 py-4 text-white/80" data-label={p('col_bank')}>{account.bank}</td>
                    ) : null}
                    {isColumnVisible('accountNumber') ? (
                      <td className="px-6 py-4 text-white/80" data-label={p('col_account_number')}>{account.accountNumber}</td>
                    ) : null}
                    {isColumnVisible('balance') ? (
                      <td className="px-6 py-4 text-center text-white font-bold" data-label={p('col_balance')}>{formatBrandMoney(account.balance)}</td>
                    ) : null}
                    {isColumnVisible('status') ? (
                      <td className="px-6 py-4 text-center" data-label={t('common.status')}>
                        <div className="flex justify-center">
                          <QawaemClickableStatusBadge
                            status={normalizeAccountStatus(account)}
                            recordId={account.id}
                            options={clickableStatusOptions}
                            onChange={handleStatusChange}
                            title={t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' })}
                          />
                        </div>
                      </td>
                    ) : null}
                    <QawaemTableStickyActionsTd>
                      <QawaemErpRowActions
                        onView={() => handleView(account)}
                        onEdit={() => handleEdit(account)}
                        onDelete={() => handleDelete(account.id)}
                        viewTitle={p('view_details_title')}
                        editTitle={t('common.edit')}
                        deleteTitle={t('common.delete')}
                      />
                    </QawaemTableStickyActionsTd>
                  </tr>
                ))
              ) : (
                <tr><td colSpan={tableColSpan} className="text-center py-12 text-white/60"><Wallet size={48} className="mx-auto mb-4 text-white/30" /><p className="text-lg">{p('no_bank_accounts')}</p></td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Account Modal */}
      <QawaemErpCrudModal
        open={showModal}
        onClose={resetForm}
        title={editingAccount ? p('modal_edit_account') : p('modal_new_account')}
        icon={Wallet}
        showSaveButton
        formId="treasury-account-form"
        saveLabel={editingAccount ? t('common.edit') : p('btn_add')}
        maxWidth="700px"
        testId="treasury-account-modal"
      >
        <QawaemErpModalFormShell
          isLight={isLight}
          tabs={accountFormTabs}
          activeTab={accountFormTab}
          onTabChange={setAccountFormTab}
          ariaLabel={editingAccount ? p('modal_edit_account') : p('modal_new_account')}
          formId="treasury-account-form"
          onSubmit={handleSubmit}
        >
          {(accountFormTabs.length === 0 || accountFormTab === 'account') && (
            <div className={CAISSE_FORM_GRID}>
              <div className="min-w-0">
                <label className={cfgTheme.label}>{p('label_account_type')} <span className="qawaem-required-mark">*</span></label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  className={cfgTheme.input}
                  required
                >
                  <option value="banque">{p('option_bank_account')}</option>
                  <option value="caisse">{p('option_caisse')}</option>
                </select>
              </div>
              <div className="min-w-0">
                <label className={cfgTheme.label}>{p('label_account_name')} <span className="qawaem-required-mark">*</span></label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className={cfgTheme.input}
                  placeholder={p('placeholder_account_name')}
                  required
                />
              </div>
              {formData.type === 'banque' && (
                <>
                  <div className="min-w-0">
                    <label className={cfgTheme.label}>{p('label_bank')}</label>
                    <input
                      type="text"
                      value={formData.bank}
                      onChange={(e) => setFormData({ ...formData, bank: e.target.value })}
                      className={cfgTheme.input}
                      placeholder={p('placeholder_bank')}
                      required
                    />
                  </div>
                  <div className="min-w-0">
                    <label className={cfgTheme.label}>{p('label_account_number')}</label>
                    <input
                      type="text"
                      value={formData.accountNumber}
                      onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value })}
                      className={cfgTheme.input}
                      placeholder={p('placeholder_account_number')}
                      required
                    />
                  </div>
                </>
              )}
              {accountFormTabs.length === 0 ? (
                <>
                  <div className="min-w-0">
                    <label className={cfgTheme.label}>{p('label_initial_balance')}</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.balance}
                      onChange={(e) => setFormData({ ...formData, balance: parseFloat(e.target.value) || 0 })}
                      className={cfgTheme.input}
                      placeholder={p('placeholder_balance')}
                      required
                    />
                  </div>
                  <div className="min-w-0 md:col-span-3">
                    <label className={cfgTheme.label}>{p('label_notes')}</label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      className={cfgTheme.input}
                      rows={3}
                      placeholder={p('placeholder_notes')}
                    />
                  </div>
                </>
              ) : null}
            </div>
          )}
          {accountFormTabs.length > 0 && accountFormTab === 'balance' && (
            <div className={CAISSE_FORM_GRID}>
              <div className="min-w-0">
                <label className={cfgTheme.label}>{p('label_initial_balance')}</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.balance}
                  onChange={(e) => setFormData({ ...formData, balance: parseFloat(e.target.value) || 0 })}
                  className={cfgTheme.input}
                  placeholder={p('placeholder_balance')}
                  required
                />
              </div>
              <div className="min-w-0 md:col-span-3">
                <label className={cfgTheme.label}>{p('label_notes')}</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className={cfgTheme.input}
                  rows={3}
                  placeholder={p('placeholder_notes')}
                />
              </div>
            </div>
          )}
        </QawaemErpModalFormShell>
      </QawaemErpCrudModal>

      {/* View Account Modal */}
      {showViewModal && viewingAccount ? (
      <QawaemErpViewModal
        open={showViewModal}
        onClose={() => { setShowViewModal(false); setViewingAccount(null); }}
        title={`${t('common.view')}: ${viewingAccount.type === 'banque' ? p('view_bank_account') : p('view_caisse')}`}
        icon={Wallet}
        maxWidth="800px"
        testId="treasury-view-modal"
      >
          <>
            <div className={`rounded-xl p-6 border mb-6 ${isLight ? 'bg-slate-50 border-slate-200' : 'bg-white/5 border-white/10'}`}>
              <div className={CAISSE_FORM_GRID}>
                <div className="space-y-1">
                  <span className={`text-sm ${cfgTheme.subtitle}`}>{p('label_account_name').replace(' *', '')}</span>
                  <p className={`font-medium ${cfgTheme.title}`}>{viewingAccount.name || '—'}</p>
                </div>
                {viewingAccount.type === 'banque' && (
                  <>
                    <div className="space-y-1">
                      <span className={`text-sm ${cfgTheme.subtitle}`}>{p('col_bank')}</span>
                      <p className={`font-medium ${cfgTheme.title}`}>{viewingAccount.bank || '—'}</p>
                    </div>
                    <div className="space-y-1">
                      <span className={`text-sm ${cfgTheme.subtitle}`}>{p('label_account_number').replace(' *', '')}</span>
                      <p className={`font-medium font-mono ${cfgTheme.title}`}>{viewingAccount.accountNumber || '—'}</p>
                    </div>
                  </>
                )}
                <div className="space-y-1">
                  <span className={`text-sm ${cfgTheme.subtitle}`}>{p('col_balance')}</span>
                  <p className={`font-bold text-lg ${cfgTheme.title}`}>{formatBrandMoney(viewingAccount.balance || 0)}</p>
                </div>
                {viewingAccount.description && (
                  <div className="space-y-1 md:col-span-3">
                    <span className={`text-sm ${cfgTheme.subtitle}`}>{p('label_description')}</span>
                    <p className={`font-medium ${cfgTheme.title}`}>{viewingAccount.description}</p>
                  </div>
                )}
              </div>
            </div>

            {(viewingAccount.created_at || viewingAccount.updated_at) && (
              <div className={`rounded-xl p-6 border mb-6 ${isLight ? 'bg-slate-50 border-slate-200' : 'bg-white/5 border-white/10'}`}>
                <h4 className={`text-xl font-semibold mb-4 flex items-center gap-2 ${isLight ? 'text-blue-700' : 'text-blue-300'}`}>
                  <FileText className="w-5 h-5" />
                  {p('section_system_info')}
                </h4>
                <div className={CAISSE_FORM_GRID}>
                  {viewingAccount.created_at && (
                    <div className="space-y-1">
                      <span className={`text-sm ${cfgTheme.subtitle}`}>{t('common.created_at')}</span>
                      <p className={`font-medium ${cfgTheme.title}`}>{new Date(viewingAccount.created_at).toLocaleString()}</p>
                    </div>
                  )}
                  {viewingAccount.updated_at && (
                    <div className="space-y-1">
                      <span className={`text-sm ${cfgTheme.subtitle}`}>{t('common.updated_at')}</span>
                      <p className={`font-medium ${cfgTheme.title}`}>{new Date(viewingAccount.updated_at).toLocaleString()}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className={`flex justify-end gap-4 pt-6 border-t ${isLight ? 'border-slate-200' : 'border-white/10'}`}>
              <button
                type="button"
                className={cfgTheme.btnGhost}
                onClick={() => setShowViewModal(false)}
              >
                {t('common.close')}
              </button>
              <button
                type="button"
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition-all duration-300 flex items-center gap-2 shadow-lg shadow-blue-500/30"
                onClick={() => {
                  setShowViewModal(false);
                  handleEdit(viewingAccount);
                }}
              >
                {t('common.edit')}
              </button>
            </div>
          </>
      </QawaemErpViewModal>
      ) : null}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </div>
  );
};

export default TreasuryPage;

